import * as LabelPrimitive from '@radix-ui/react-label';
import { cva } from 'class-variance-authority';
import { Eye, EyeOff } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/utils';

const inputVariants = cva(
  'flex w-full rounded-[40px] border-2 border-solid bg-white px-4 py-3 text-sm font-medium transition-all duration-200 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-magical-moonlight-d1 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-magical-moonlight-s',
  {
    variants: {
      variant: {
        default:
          'border-magical-moonlight-d1 text-magical-moonlight-dark focus-visible:border-bhanzu-orange focus-visible:ring-2 focus-visible:ring-bhanzu-orange/20',
        error:
          'border-fusion-red text-magical-moonlight-dark focus-visible:border-fusion-red focus-visible:ring-2 focus-visible:ring-fusion-red/20',
        success:
          'border-success-green text-magical-moonlight-dark focus-visible:border-success-green focus-visible:ring-2 focus-visible:ring-success-green/20',
        active:
          'border-magical-moonlight-dark text-magical-moonlight-dark focus-visible:border-bhanzu-orange focus-visible:ring-2 focus-visible:ring-bhanzu-orange/20',
      },
      size: {
        // Desktop size (default)
        default: 'h-12 px-4 py-3 text-sm',
        // Tablet size
        md: 'h-11 px-4 py-2.5 text-sm',
        // Mobile size
        sm: 'h-10 px-3 py-2 text-xs',
        // Large size
        lg: 'h-14 px-5 py-4 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const labelVariants = cva(
  'absolute left-4 pointer-events-none transition-all duration-200 ease-in-out origin-left text-sm font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
  {
    variants: {
      variant: {
        default: 'text-magical-moonlight-d1 peer-focus:text-bhanzu-orange',
        error: 'text-fusion-red peer-focus:text-fusion-red',
        success: 'text-success-green peer-focus:text-success-green',
        active: 'text-magical-moonlight-dark peer-focus:text-bhanzu-orange',
      },
      floating: {
        true: 'top-2 scale-75 -translate-y-4 bg-white px-1 text-xs',
        false: 'top-1/2 -translate-y-1/2 scale-100',
      },
    },
    defaultVariants: {
      variant: 'default',
      floating: false,
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  variant?: 'default' | 'error' | 'success' | 'active';
  size?: 'sm' | 'default' | 'md' | 'lg';
  label?: string;
  labelClassName?: string;
  helperText?: string;
  errorText?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  startIconClassName?: string;
  endIconClassName?: string;
  forceFloat?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      size,
      type,
      label,
      labelClassName,
      helperText,
      errorText,
      id,
      value,
      defaultValue,
      startIcon,
      endIcon,
      startIconClassName,
      endIconClassName,
      forceFloat,
      ...props
    },
    ref
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(
      Boolean(value ?? defaultValue)
    );
    const isPassword = type === 'password';
    const generatedId = React.useId();
    const inputId = id ?? generatedId;

    const shouldFloat =
      isFocused || hasValue || Boolean(props.placeholder) || forceFloat;

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(Boolean(e.target.value));
      props.onChange?.(e);
    };

    React.useEffect(() => {
      setHasValue(Boolean(value ?? defaultValue));
    }, [value, defaultValue]);

    return (
      <div className="relative w-full">
        <div className="relative">
          {startIcon ? (
            <div
              className={cn(
                'absolute left-4 top-1/2 -translate-y-1/2 text-magical-moonlight-d1 pointer-events-none z-(--z-nominal)',
                startIconClassName
              )}
            >
              {startIcon}
            </div>
          ) : null}
          <input
            id={inputId}
            type={isPassword && showPassword ? 'text' : type}
            className={cn(
              inputVariants({
                variant: errorText ? 'error' : variant,
                size,
                className,
              }),
              label && 'peer',
              // Responsive design classes
              'sm:h-10 sm:text-xs', // Mobile
              'md:h-11 md:text-sm', // Tablet
              'lg:h-12 lg:text-sm', // Desktop
              startIcon && 'pl-11',
              endIcon && 'pr-11'
            )}
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            {...props}
          />
          {label ? (
            <LabelPrimitive.Root
              htmlFor={inputId}
              className={cn(
                labelVariants({
                  variant: errorText ? 'error' : variant,
                  floating: shouldFloat,
                }),
                // Float the label when the browser autofills saved credentials.
                // Autofill sets the value without a React change event, so we
                // rely on the CSS :autofill state instead of component state.
                'peer-autofill:top-2 peer-autofill:-translate-y-4 peer-autofill:scale-75 peer-autofill:bg-white peer-autofill:px-1 peer-autofill:text-xs',
                startIcon && 'left-12',
                labelClassName
              )}
            >
              {label}
            </LabelPrimitive.Root>
          ) : null}
          {endIcon && !isPassword ? (
            <div
              className={cn(
                'absolute right-4 top-1/2 -translate-y-1/2 text-magical-moonlight-d1 pointer-events-none z-(--z-nominal)',
                endIconClassName
              )}
            >
              {endIcon}
            </div>
          ) : null}
          {isPassword ? (
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-magical-moonlight-d1 hover:text-bhanzu-orange transition-colors z-(--z-nominal)"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </button>
          ) : null}
        </div>
        {/* Helper text or error text */}
        {(Boolean(helperText) || Boolean(errorText)) && (
          <div className="mt-1 px-1">
            {Boolean(errorText) && (
              <p className="text-xs text-fusion-red font-medium">{errorText}</p>
            )}
            {!errorText && Boolean(helperText) && (
              <p className="text-xs text-bhanzu-orange font-medium">
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export default Input;
export { inputVariants, labelVariants };
